import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldX, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUserStore } from '@/store/useUserStore';
import { getFirstAccessiblePath } from '@/lib/permission';

const REDIRECT_SECONDS = 10;

export const AccessDenied = () => {
  const navigate = useNavigate();
  const token = useUserStore((state) => state.token);
  const user = useUserStore((state) => state.user);
  const role = useUserStore((state) => state.role);
  const logout = useUserStore((state) => state.logout);

  const isAuthenticated = !!(token && user);
  // Signed-in users are eased back to the first page they can actually reach;
  // signed-out users go to the login screen.
  const redirectTarget = isAuthenticated
    ? getFirstAccessiblePath(role, user?.permissions ?? [])
    : '/';

  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const [cancelled, setCancelled] = useState(false);
  const settledRef = useRef(false);

  const handleLogout = () => {
    settledRef.current = true;
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (cancelled) return;
    const id = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          if (!settledRef.current) {
            settledRef.current = true;
            navigate(redirectTarget, { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelled]);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-linear-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center">
            <ShieldX className="w-12 h-12 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          Page Not Accessible
        </h1>

        {/* Description */}
        <p className="text-gray-600 mb-2">
          You don't have permission to access this page.
        </p>
        <p className="text-sm text-gray-500 mb-4">
          This page may require specific roles or privileges that your account doesn't have.
        </p>

        {/* Auto-redirect notice */}
        {!cancelled ? (
          <p className="text-sm text-gray-600 mb-8">
            Redirecting you to {isAuthenticated ? 'a page you can access' : 'the login page'} in{' '}
            <span className="font-semibold text-gray-900">{seconds}s</span>&hellip;{' '}
            <button
              onClick={() => setCancelled(true)}
              className="font-medium text-[#3C3295] hover:underline"
            >
              Stay on this page
            </button>
          </p>
        ) : (
          <p className="text-sm text-gray-400 mb-8">Automatic redirect cancelled.</p>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="w-full sm:w-auto flex items-center justify-center gap-2"
          >
            <ArrowLeft size={16} />
            Go Back
          </Button>
          <Button
            onClick={() => handleLogout()}
            className="w-full sm:w-auto flex items-center justify-center bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white transition-colors"
          >
            Go to Login
          </Button>
        </div>

        {/* Footer Help Text */}
        <p className="mt-8 text-xs text-gray-400">
          If you believe this is an error, please contact your administrator.
        </p>
      </div>
    </div>
  );
};
